import { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Divider,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  KeyOutlined,
  PlusOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { api } from "../api";
import { useAuth } from "../contexts/AuthContext";

const { Title, Text } = Typography;

type UserRow = { id: number; username: string; role: string };

const ROLE_OPTIONS = [
  { label: "Admin",    value: "admin" },
  { label: "Operator", value: "operator" },
  { label: "Readonly", value: "readonly" },
];

const roleColor: Record<string, string> = {
  admin: "red",
  operator: "blue",
  readonly: "default",
};

// ── Create User modal ─────────────────────────────────────────────────────────

function CreateUserModal({ open, onClose, onCreated }: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    let vals: { username: string; password: string; confirm: string; role: string };
    try { vals = await form.validateFields(); } catch { return; }
    if (vals.password !== vals.confirm) {
      message.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.createUser(vals.username, vals.password, vals.role);
      message.success(`User "${vals.username}" created`);
      form.resetFields();
      onCreated();
      onClose();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<><PlusOutlined /> Create User</>}
      open={open}
      onOk={submit}
      onCancel={() => { form.resetFields(); onClose(); }}
      confirmLoading={loading}
      okText="Create"
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
        <Form.Item name="username" label="Username" rules={[{ required: true, message: "Required" }]}>
          <Input prefix={<UserOutlined />} placeholder="username" autoFocus />
        </Form.Item>
        <Form.Item name="role" label="Role" initialValue="readonly" rules={[{ required: true }]}>
          <Select options={ROLE_OPTIONS} />
        </Form.Item>
        <Form.Item name="password" label="Password"
          rules={[{ required: true }, { min: 8, message: "At least 8 characters" }]}>
          <Input.Password placeholder="min. 8 characters" />
        </Form.Item>
        <Form.Item name="confirm" label="Confirm password" rules={[{ required: true }]}>
          <Input.Password placeholder="repeat password" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Change Password modal ─────────────────────────────────────────────────────

function ChangePasswordModal({ open, target, isAdmin, isSelf, onClose }: {
  open: boolean;
  target: UserRow | null;
  isAdmin: boolean;
  isSelf: boolean;
  onClose: () => void;
}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    let vals: { current_password?: string; new_password: string; confirm: string };
    try { vals = await form.validateFields(); } catch { return; }
    if (vals.new_password !== vals.confirm) {
      message.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.changePassword({
        target_username: isSelf ? undefined : target?.username,
        current_password: vals.current_password,
        new_password: vals.new_password,
      });
      message.success("Password changed successfully");
      form.resetFields();
      onClose();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  // Admin changing another user's pw → no current password needed
  const needCurrent = !isAdmin || isSelf;

  return (
    <Modal
      title={<><KeyOutlined /> Change Password — {target?.username}</>}
      open={open}
      onOk={submit}
      onCancel={() => { form.resetFields(); onClose(); }}
      confirmLoading={loading}
      okText="Change"
      destroyOnClose
    >
      <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
        {needCurrent && (
          <Form.Item name="current_password" label="Current password" rules={[{ required: true }]}>
            <Input.Password autoFocus />
          </Form.Item>
        )}
        <Form.Item name="new_password" label="New password"
          rules={[{ required: true }, { min: 8, message: "At least 8 characters" }]}>
          <Input.Password autoFocus={!needCurrent} placeholder="min. 8 characters" />
        </Form.Item>
        <Form.Item name="confirm" label="Confirm new password" rules={[{ required: true }]}>
          <Input.Password />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// ── Edit Role modal ───────────────────────────────────────────────────────────

function EditRoleModal({ open, target, onClose, onSaved }: {
  open: boolean;
  target: UserRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [role, setRole] = useState(target?.role ?? "readonly");
  const [loading, setLoading] = useState(false);

  useEffect(() => { setRole(target?.role ?? "readonly"); }, [target]);

  const submit = async () => {
    if (!target) return;
    setLoading(true);
    try {
      await api.updateUserRole(target.id, role);
      message.success("Role updated");
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={<><EditOutlined /> Edit Role — {target?.username}</>}
      open={open}
      onOk={submit}
      onCancel={onClose}
      confirmLoading={loading}
      okText="Save"
      destroyOnClose
    >
      <div style={{ marginTop: 16, marginBottom: 8 }}>
        <Text style={{ fontSize: 12, marginRight: 8 }}>Role:</Text>
        <Select value={role} onChange={setRole} options={ROLE_OPTIONS} style={{ width: 160 }} />
      </div>
    </Modal>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function UserManagement() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [pwTarget, setPwTarget] = useState<UserRow | null>(null);
  const [roleTarget, setRoleTarget] = useState<UserRow | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.listUsers();
      setUsers(list);
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const deleteUser = async (u: UserRow) => {
    try {
      await api.deleteUser(u.id);
      message.success(`User "${u.username}" deleted`);
      reload();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  const columns: ColumnsType<UserRow> = [
    { title: "ID", dataIndex: "id", width: 60 },
    {
      title: "Username",
      dataIndex: "username",
      render: (v: string) => (
        <Space>
          <UserOutlined style={{ color: "var(--muted)" }} />
          <Text strong>{v}</Text>
          {v === user?.username && <Badge color="green" text="you" style={{ fontSize: 11 }} />}
        </Space>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      width: 120,
      render: (v: string) => <Tag color={roleColor[v] ?? "default"}>{v}</Tag>,
    },
    {
      title: "",
      width: 160,
      render: (_: unknown, row: UserRow) => (
        <Space>
          {/* Change password — everyone can change their own; admin can change any */}
          {(isAdmin || row.username === user?.username) && (
            <Button
              size="small"
              icon={<KeyOutlined />}
              title="Change password"
              onClick={() => setPwTarget(row)}
            />
          )}
          {/* Edit role — admin only, cannot edit self's role */}
          {isAdmin && row.username !== user?.username && (
            <Button
              size="small"
              icon={<EditOutlined />}
              title="Edit role"
              onClick={() => setRoleTarget(row)}
            />
          )}
          {/* Delete — admin only, cannot delete self */}
          {isAdmin && row.username !== user?.username && (
            <Popconfirm
              title={`Delete user "${row.username}"?`}
              okText="Delete"
              okButtonProps={{ danger: true }}
              onConfirm={() => deleteUser(row)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 720 }}>
      <Title level={5} style={{ marginTop: 0, marginBottom: 16, color: "var(--strong)" }}>
        User Management
      </Title>

      <Card
        size="small"
        title="Accounts"
        extra={
          isAdmin && (
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setCreateOpen(true)}
            >
              Add user
            </Button>
          )
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          dataSource={users}
          columns={columns}
          pagination={false}
          size="small"
        />
      </Card>

      {!isAdmin && (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <Card size="small" title={<><KeyOutlined /> Change My Password</>} style={{ maxWidth: 420 }}>
            <ChangePasswordSelf username={user?.username ?? ""} />
          </Card>
        </>
      )}

      {/* Modals */}
      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={reload}
      />
      <ChangePasswordModal
        open={pwTarget !== null}
        target={pwTarget}
        isAdmin={isAdmin}
        isSelf={pwTarget?.username === user?.username}
        onClose={() => setPwTarget(null)}
      />
      <EditRoleModal
        open={roleTarget !== null}
        target={roleTarget}
        onClose={() => setRoleTarget(null)}
        onSaved={reload}
      />
    </div>
  );
}

// ── inline change-own-password form (for non-admin) ───────────────────────────

function ChangePasswordSelf({ username }: { username: string }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    let vals: { current: string; newpw: string; confirm: string };
    try { vals = await form.validateFields(); } catch { return; }
    if (vals.newpw !== vals.confirm) {
      message.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.changePassword({
        target_username: username,
        current_password: vals.current,
        new_password: vals.newpw,
      });
      message.success("Password changed");
      form.resetFields();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form form={form} layout="vertical" style={{ marginTop: 4 }}>
      <Form.Item name="current" label="Current password" rules={[{ required: true }]}>
        <Input.Password />
      </Form.Item>
      <Form.Item name="newpw" label="New password"
        rules={[{ required: true }, { min: 8, message: "At least 8 characters" }]}>
        <Input.Password />
      </Form.Item>
      <Form.Item name="confirm" label="Confirm new password" rules={[{ required: true }]}>
        <Input.Password />
      </Form.Item>
      <Button type="primary" loading={loading} onClick={submit} icon={<KeyOutlined />}>
        Change Password
      </Button>
    </Form>
  );
}
